import axios from "axios";
import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import "./_RepairStyleManager.scss";
import { getApiEndpoint, getImageUrl } from "../../../../utils/apiConfig";
import axiosWithAuth from "../../../../utils/axiosWithAuth";

const RepairStyleManager = () => {
  const [repairStyleAdmin, setRepairStyleAdmin] = useState([]);
  const [repairStyle, setRepairStyle] = useState({
    name: { az: "", en: "", ru: "" },
    image: {
      id: null,
      status: true,
    },
    status: true,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const authToken = localStorage.getItem("authToken");

  useEffect(() => {
    fetchRepairStyles();
  }, []);

  const fetchRepairStyles = () => {
    axiosWithAuth()
      .get("repair-styles/admin")
      .then((response) => {
        setRepairStyleAdmin(response.data);
      })
      .catch((error) => console.error("Failed to fetch repair styles:", error));
  };

  const handleInput = (event) => {
    const { name, value, type, checked } = event.target;
    if (type === "checkbox") {
      setRepairStyle((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setRepairStyle((prev) => ({
        ...prev,
        name: { ...prev.name, [name]: value },
      }));
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      await axiosWithAuth().put(
        `repair-styles/${id}`,
        { status: !currentStatus }
      );
      fetchRepairStyles();
      Swal.fire({
        title: "Success!",
        text: "Status updated successfully",
        icon: "success",
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error("Failed to update status:", error);
      Swal.fire({
        title: "Error!",
        text: error.response?.data?.message || "Failed to update status",
        icon: "error",
        timer: 2000,
        showConfirmButton: false
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await axiosWithAuth().put(
          `repair-styles/${editId}`,
          repairStyle
        );
        Swal.fire({
          title: "Success!",
          text: "Repair style updated successfully",
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        await axiosWithAuth().post(
          "repair-styles",
          repairStyle
        );
        Swal.fire({
          title: "Success!",
          text: "Repair style added successfully",
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });
      }
      setIsEditing(false);
      setEditId(null);
      setRepairStyle({
        name: { az: "", en: "", ru: "" },
        image: { id: null },
        status: true,
      });
      fetchRepairStyles();
    } catch (error) {
      console.error("Failed to save repair style:", error);
      Swal.fire({
        title: "Error!",
        text: error.response?.data?.message || "Failed to save repair style",
        icon: "error",
        timer: 2000,
        showConfirmButton: false
      });
    }
  };

  const handleEdit = (style) => {
    setIsEditing(true);
    setEditId(style.id);
    setRepairStyle({
      name: style.name,
      image: style.image,
      status: style.status,
    });
  };

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        try {
          await axiosWithAuth().delete(`repair-styles/${id}`);
          fetchRepairStyles();
          Swal.fire(
            'Deleted!',
            'Repair style has been deleted.',
            'success'
          );
        } catch (error) {
          // If the delete fails because of relationships
          if (error.response && error.response.status === 409) {
            const forceResult = await Swal.fire({
              title: 'Force Delete?',
              text: "This repair style is in use. Force delete will remove all references. This cannot be undone!",
              icon: 'warning',
              showCancelButton: true,
              confirmButtonColor: '#d33',
              cancelButtonColor: '#3085d6',
              confirmButtonText: 'Force Delete'
            });

            if (forceResult.isConfirmed) {
              await axiosWithAuth().delete(`repair-styles/${id}?force=true`);
              fetchRepairStyles();
              Swal.fire(
                'Force Deleted!',
                'Repair style and all its references have been deleted.',
                'success'
              );
            }
          } else {
            throw error;
          }
        }
      }
    } catch (error) {
      console.error("Failed to delete repair style:", error);
      Swal.fire(
        'Error!',
        'Failed to delete repair style.',
        'error'
      );
    }
  };

  const uploadImage = (file) => {
    const formData = new FormData();
    formData.append("file", file);

    return axiosWithAuth().post(
      "mono-files/upload",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const response = await uploadImage(file);
        setRepairStyle((prev) => ({
          ...prev,
          image: response.data.id
        }));
      } catch (error) {
        console.error("Failed to upload image:", error);
        Swal.fire({
          title: "Error!",
          text: "Failed to upload image",
          icon: "error",
          timer: 2000,
          showConfirmButton: false
        });
      }
    }
  };

  return (
    <div className="repair-style-manager">
      <h2>Repair Style Manager</h2>

      <div className="form-section">
        <h3>{isEditing ? "Edit Repair Style" : "Add New Repair Style"}</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name (Azerbaijani)</label>
            <input
              type="text"
              name="az"
              value={repairStyle.name.az}
              onChange={handleInput}
              required
            />
          </div>
          <div className="form-group">
            <label>Name (English)</label>
            <input
              type="text"
              name="en"
              value={repairStyle.name.en}
              onChange={handleInput}
              required
            />
          </div>
          <div className="form-group">
            <label>Name (Russian)</label>
            <input
              type="text"
              name="ru"
              value={repairStyle.name.ru}
              onChange={handleInput}
              required
            />
          </div>
          <div className="form-group">
            <label>Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              required={!isEditing}
            />
          </div>
          <div className="form-group checkbox-group">
            <input
              type="checkbox"
              name="status"
              checked={repairStyle.status}
              onChange={handleInput}
            />
            <label>Active</label>
          </div>
          <div className="button-group">
            <button type="submit" className="submit-btn">
              {isEditing ? "Update" : "Add"}
            </button>
            {isEditing && (
              <button
                type="button"
                className="cancel-btn"
                onClick={() => {
                  setIsEditing(false);
                  setEditId(null);
                  setRepairStyle({
                    name: { az: "", en: "", ru: "" },
                    image: {
                      id: null,
                      status: true,
                    },
                    status: true,
                  });
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="table-section">
        <table>
          <thead>
            <tr>
              <th>Image</th>
              <th>Name (AZ)</th>
              <th>Name (EN)</th>
              <th>Name (RU)</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {repairStyleAdmin.map((style) => (
              <tr key={style.id}>
                <td>
                  {style.image ? (
                    <img
                      src={getImageUrl(style.image)}
                      alt={style.name.az}
                      style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                    />
                  ) : (
                    <span>No image</span>
                  )}
                </td>
                <td>{style.name.az}</td>
                <td>{style.name.en}</td>
                <td>{style.name.ru}</td>
                <td>
                  <span
                    className={`status-badge ${
                      style.status ? "active" : "inactive"
                    }`}
                    onClick={() => handleStatusToggle(style.id, style.status)}
                  >
                    {style.status ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="edit-btn"
                      onClick={() => handleEdit(style)}
                    >
                      Edit
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(style.id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RepairStyleManager;
